import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../../../ui/components/Button";
import { Select } from "../../../ui/components/Select";
import { TextField } from "../../../ui/components/TextField";
import { TextArea } from "../../../ui/components/TextArea";
import {
  VendorContextData,
  generateVendorContext,
} from "../../../services/contextApi";
import { useToast } from "./Toast/ToastContext";

interface VendorContextSectionProps {
  vendorName: string;
  existingContext?: VendorContextData;
  onContextChange: (context: VendorContextData) => void;
}

const VendorContextSection: React.FC<VendorContextSectionProps> = ({
  vendorName,
  existingContext,
  onContextChange,
}) => {
  const { showToast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [contextData, setContextData] = useState<VendorContextData>({
    vendor_name: vendorName,
    default_expense_category: "",
    gst_applicable: "Y",
    gst_rate: undefined,
    recurring_billing: "N",
    raw_input: "",
    final_context: "",
  });

  useEffect(() => {
    if (existingContext) {
      setContextData((prev) => ({ ...prev, ...existingContext }));
    }
  }, [existingContext]);

  useEffect(() => {
    setContextData((prev) => ({ ...prev, vendor_name: vendorName }));
  }, [vendorName]);

  const handleFieldChange = useCallback(
    (field: keyof VendorContextData, value: string | number | undefined) => {
      setContextData((prev) => {
        const updated = { ...prev, [field]: value };
        onContextChange(updated);
        return updated;
      });
    },
    [onContextChange]
  );

  const handleGenerate = async () => {
    const name = contextData.vendor_name || vendorName;
    if (!name?.trim()) {
      showToast("Vendor name is required", "error");
      return;
    }
    if (!contextData.raw_input?.trim()) {
      showToast("Please describe what this vendor supplies", "error");
      return;
    }
    setIsGenerating(true);
    try {
      const response = await generateVendorContext({
        vendor_name: name,
        default_expense_category: contextData.default_expense_category,
        gst_applicable: contextData.gst_applicable,
        gst_rate: contextData.gst_rate,
        recurring_billing: contextData.recurring_billing,
        raw_input: contextData.raw_input,
      });
      if (response.success && response.data?.ai_rewritten_context) {
        const aiText = response.data.ai_rewritten_context;
        setContextData((prev) => {
          const updated = {
            ...prev,
            ai_rewritten_context: aiText,
            final_context: aiText,
          };
          onContextChange(updated);
          return updated;
        });
      }
    } catch (error: any) {
      showToast(error?.message || "Failed to generate context", "error");
    } finally {
      setIsGenerating(false);
    }
  };

  const charCount = contextData.final_context?.length || 0;

  return (
    <div className="flex w-full flex-col items-start gap-6">
      <span className="text-heading-3 font-heading-3 text-default-font">
        GL Classification Context
      </span>
      <p className="text-body font-body text-subtext-color">
        Provide context about this vendor to improve automatic GL classification
        accuracy.
      </p>

      <div className="w-full items-start gap-4 grid grid-cols-2">
        <TextField className="w-full" label="Default Expense Category">
          <TextField.Input
            placeholder="e.g. Cloud Hosting, Office Rent, Legal Fees"
            value={contextData.default_expense_category || ""}
            onChange={(e) =>
              handleFieldChange("default_expense_category", e.target.value)
            }
          />
        </TextField>
        <Select
          label="GST Applicable"
          value={contextData.gst_applicable || "Y"}
          onValueChange={(value) => handleFieldChange("gst_applicable", value)}
        >
          <Select.Item value="Y">Yes</Select.Item>
          <Select.Item value="N">No</Select.Item>
        </Select>
      </div>

      <div className="w-full items-start gap-4 grid grid-cols-2">
        <TextField className="w-full" label="GST Rate (%)">
          <TextField.Input
            placeholder="e.g. 18"
            value={contextData.gst_rate?.toString() || ""}
            onChange={(e) => {
              const val = e.target.value;
              handleFieldChange(
                "gst_rate",
                val === "" ? undefined : parseFloat(val)
              );
            }}
          />
        </TextField>
        <Select
          label="Recurring Billing"
          value={contextData.recurring_billing || "N"}
          onValueChange={(value) =>
            handleFieldChange("recurring_billing", value)
          }
        >
          <Select.Item value="Y">Yes</Select.Item>
          <Select.Item value="N">No</Select.Item>
        </Select>
      </div>

      <TextArea className="w-full" label="What does this vendor supply to your company?">
        <TextArea.Input
          placeholder="e.g. Provides monthly cloud hosting services for our SaaS platform, including server maintenance and CDN..."
          value={contextData.raw_input || ""}
          onChange={(e) => handleFieldChange("raw_input", e.target.value)}
        />
      </TextArea>

      <Button
        variant="neutral-secondary"
        onClick={handleGenerate}
        disabled={isGenerating}
        loading={isGenerating}
      >
        {isGenerating ? "Generating..." : "Generate AI Context"}
      </Button>

      <div className="flex w-full flex-col gap-2">
        <div className="flex w-full items-center justify-between">
          <span className="text-label font-label text-default-font">
            Final Context
          </span>
          <span
            className={`text-caption font-caption ${
              charCount > 750 ? "text-error-600" : "text-subtext-color"
            }`}
          >
            {charCount}/750
          </span>
        </div>
        <TextArea className="w-full">
          <TextArea.Input
            value={contextData.final_context || ""}
            onChange={(e) => {
              const value = e.target.value.slice(0, 750);
              handleFieldChange("final_context", value);
            }}
            placeholder="Generate using the button above, or type your own context here..."
          />
        </TextArea>
      </div>
    </div>
  );
};

export default VendorContextSection;
